import {
    GetTimesheetsReportsResponse,
    GetTimesheetsTasksResponse,
    TaskState,
    useGetTimesheetsReportsMutation,
    useGetTimesheetsTasksMutation
} from '@services';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Slider from 'react-slick';

export const usePointageSlider = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const weekParam = searchParams.get('week');
    const isInitialMount = useRef(true);

    const [reports, setReports] = useState<GetTimesheetsReportsResponse>([]);
    const [tasks, setTasks] = useState<GetTimesheetsTasksResponse>([]);

    const [nav1, setNav1] = useState<Slider | null>(null);

    const [currentIndex, setCurrentIndex] = useState<number>(weekParam ? parseInt(weekParam) : 0);
    const [summedHours, setSummedHours] = useState<number>(0);

    const [dayToPointage, setDayToPointage] = useState<string>();

    const [getTimesheetsReports, { data: timesheetsReports, isLoading: isTimesheetsReportsLoading }] = useGetTimesheetsReportsMutation();

    const [getTimesheetsTasks, { data: timesheetsTasks, isLoading: isTimesheetsTasksLoading }] = useGetTimesheetsTasksMutation();

    useEffect(() => {
        getTimesheetsReports(undefined);
        getTimesheetsTasks(undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isTimesheetsReportsLoading) {
            return;
        }

        if (timesheetsReports) {
            setReports(timesheetsReports);
        }
    }, [timesheetsReports, isTimesheetsReportsLoading]);

    useEffect(() => {
        if (isTimesheetsTasksLoading) {
            return;
        }

        if (timesheetsTasks) {
            setTasks(timesheetsTasks);
        }
    }, [timesheetsTasks, isTimesheetsTasksLoading]);

    useEffect(() => {
        if (reports.length > 0) {
            const currentReport = reports[currentIndex];
            const summedHours = currentReport
                ? currentReport.days.reduce((acc, day) => acc + Number(day.tasks.reduce((acc, task) => acc + Number(task.hours), 0)), 0)
                : 0;
            setSummedHours(summedHours);
        }
    }, [currentIndex, reports]);

    useEffect(() => {
        if (!reports?.length) {
            return;
        }

        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        const currentWeekParam = searchParams.get('week');
        if (currentWeekParam !== currentIndex.toString()) {
            setSearchParams({ week: currentIndex.toString() }, { replace: true });
        }
    }, [currentIndex, searchParams, setSearchParams, reports]);

    useEffect(() => {
        if (reports.length > 0) {
            let foundDay = null;
            for (const report of reports) {
                for (const day of report.days) {
                    const isSaturday = new Date(day.date).getDay() === 6;
                    if (day.tasks.length === 0 && !isSaturday && !day.holidayLabel && day.canValidate) {
                        setDayToPointage(day.id);
                        foundDay = day.id;
                        break;
                    }
                }
                if (foundDay) break;
            }
        }
    }, [reports, currentIndex]);

    useEffect(() => {
        if (!reports?.length || weekParam) {
            return;
        }

        for (let i = 0; i < reports.length; i++) {
            const report = reports[i];
            const hasEligibleDay = report.days.some((day) => {
                return (
                    (day.tasks.length === 0 || day.tasks.some((task) => task.state_id === TaskState.WAITING)) &&
                    !day.optional &&
                    !day.holidayLabel
                );
            });

            if (hasEligibleDay) {
                setCurrentIndex(i);
                setSearchParams({ week: i.toString() }, { replace: true });
                nav1?.slickGoTo(i);
                break;
            }
        }
    }, [reports, weekParam, setSearchParams, nav1]);

    return {
        isLoading: isTimesheetsReportsLoading,
        reports,
        tasks,
        nav1,
        setNav1,
        setCurrentIndex,
        currentIndex,
        summedHours,
        dayToPointage
    };
};
